import React from 'react';
import { DelayedQuote } from '@benzinga/quotes-manager';
import { getColorByValue, sanitizeHTML } from '@benzinga/frontend-utils';
import { useTheme } from '@benzinga/themetron';
import { formatLarge, formatPrice } from '@benzinga/utils/numberUtils';
import { Button, Icon } from '@benzinga/core-ui';
import { Fund } from '@benzinga/etf-manager';
import styles from './styles.module.scss';
import { faChevronsDown, faChevronsUp } from '@fortawesome/pro-regular-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { Tooltip } from '@benzinga/core-ui';

interface DelayedQuoteWithFundInfo extends DelayedQuote {
  disclosure_label?: string;
  disclosure_link?: string;
  fundInfo?: Fund;
  link?: string;
  tooltip?: string;
}

export interface ETFQuotesCardProps {
  etf_symbols?: {
    symbol: string;
    link: string;
  }[];
  etfData?: DelayedQuoteWithFundInfo[] | Record<string, DelayedQuoteWithFundInfo>;
  logoData?: any;
  title?: string;
  enable_broker_btn?: boolean;
  enable_nasdaq_stamp?: boolean;
  layout?: 'floating' | 'default';
  initial_display_items?: number;
  promo_text?: string;
}

export const ETFQuotesCard: React.FC<ETFQuotesCardProps> = ({
  enable_broker_btn,
  enable_nasdaq_stamp,
  etf_symbols,
  etfData,
  initial_display_items = -1,
  layout,
  logoData,
  promo_text,
  title,
}) => {
  const theme = useTheme();
  const [showAll, setShowAll] = React.useState<boolean>(false);

  const etfEntries = React.useMemo((): [string, DelayedQuoteWithFundInfo][] => {
    if (etfData == null) return [];
    if (Array.isArray(etfData)) {
      return etfData.map(d => [d.symbol, d]);
    }
    return Object.entries(etfData);
  }, [etfData]);

  const handleShowAll = React.useCallback(() => {
    setShowAll(previousShowAll => !previousShowAll);
  }, []);

  const splicedItems =
    initial_display_items && initial_display_items !== -1 && initial_display_items > 0
      ? etfEntries.slice(0, initial_display_items)
      : etfEntries;

  const data = showAll ? etfEntries : splicedItems;

  const canExpand =
    Boolean(initial_display_items && initial_display_items !== -1 && initial_display_items > 0) &&
    etfEntries.length > initial_display_items;

  if (!etf_symbols?.length || etf_symbols.length === 0) return null;

  return (
    <div className={`etf-card-wrapper mb-4 ${styles.etfCard} ${layout === 'floating' ? styles.layoutFloating : ''}`}>
      <div className="section-heading flex items-center justify-between">
        <h3 className={`mb-2 text-lg font-bold ${styles.sectionTitle}`}>{title}</h3>
        {promo_text && (
          <div className="flex items-end justify-end">
            <div
              className="text-right text-sm bg-gray-200 rounded px-2 py-1"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(promo_text) }}
            />
          </div>
        )}
      </div>
      <div
        className={`bg-white box-border flex flex-col justify-center p-4 shadow-sm border border-t-4 border-t-[rgb(255,90,31)] card-wrapper ${styles.cardWrapper}`}
      >
        {data.map(([symbol, etfDetail]) => (
          <div className="item-wrap py-6 border-b border-gray-200 first:pt-0 last:pb-0 last:border-b-0" key={symbol}>
            <div className={layout === 'floating' && etfDetail?.link ? styles.clickableItem : undefined}>
              {layout === 'floating' && etfDetail?.link && (
                <a
                  aria-label={`${etfDetail?.name ?? symbol} details`}
                  className={styles.stretchedLink}
                  href={etfDetail.link}
                  rel="noopener noreferrer"
                  target="_blank"
                />
              )}
              <div className={`${styles.quoteOverview} flex gap-4  w-full mb-4`}>
                <div
                  className={`flex gap-4 w-full justify-between quote-overview-wrapper ${styles.quoteOverviewWrapper}`}
                >
                  <div className="flex gap-2 min-w-0">
                    <div className="ticker-logo shrink-0">
                      {logoData?.[symbol] && (
                        <img
                          alt={`${symbol} logo`}
                          height="60"
                          src={logoData[symbol].files?.mark_composite_light}
                          width="60"
                        />
                      )}
                    </div>
                    <div className={`${styles.tickerName}`}>
                      <div className="text-gray-900 block relative">
                        <span className={`font-bold text-xl sm:text-3xl leading-none ${styles.quoteName}`}>
                          {etfDetail?.name}
                        </span>
                        {etfDetail?.tooltip && (
                          <div className={styles.cardToolTip}>
                            <Tooltip
                              className={styles.quoteTooltip}
                              content={etfDetail?.tooltip}
                              isContentHtml={true}
                              width={250}
                            >
                              <Icon
                                className={`${styles.infoCircleIcon} text-sm flex items-center justify-center`}
                                icon={faCircleInfo}
                              />
                            </Tooltip>
                          </div>
                        )}
                      </div>
                      <span className={`quote-exchange text-gray-500 font-semibold text-sm`}>
                        ({etfDetail.exchange}:{symbol})
                      </span>
                    </div>
                  </div>

                  {layout !== 'floating' && (
                    <div className="h-full flex gap-3 shrink-0">
                      <div className="price-section hidden sm:flex flex-col ml-3 text-right shrink-0">
                        <div className="price-value text-2xl font-semibold text-gray-600 leading-tight whitespace-nowrap">
                          {etfDetail?.askPrice ? `$${formatPrice(etfDetail?.askPrice)}` : '-'}
                        </div>
                        <span
                          className="price-change font-semibold whitespace-nowrap"
                          style={{
                            color: getColorByValue(theme, etfDetail.changePercent),
                          }}
                          title={etfDetail.changePercent?.toString()}
                        >
                          {`${etfDetail?.change ? `$${formatPrice(etfDetail.change, { fixedPlace: 2 })}` : '-'} (${
                            etfDetail?.changePercent ? `${etfDetail.changePercent.toFixed(2)}%` : '-'
                          })`}
                        </span>
                        <div className="text-xs italic text-gray-500 whitespace-nowrap">15 minutes delayed</div>
                      </div>

                      {!enable_broker_btn && etfDetail?.link && (
                        <div className="hidden lg:block">
                          <Button
                            as="a"
                            className="w-full min-h-[60px] min-w-[110px]"
                            href={etfDetail.link}
                            target="_blank"
                            variant="flat-blue"
                          >
                            Learn More
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className={`${styles.fundamentals} mx-auto bg-gray-300 p-px`}>
                <div
                  className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-300 text-sm ${styles.fundamentalItemsWrapper}`}
                >
                  <div className={`${styles.fundamentalItem} bg-white p-1 font-semibold flex`}>
                    <div className="label text-[#6b7280] mr-2">Open:</div>
                    <div className="value text-[#111827]">
                      {etfDetail?.open ? `$${formatPrice(etfDetail.open)}` : '-'}
                    </div>
                  </div>

                  <div className={`${styles.fundamentalItem} bg-white p-1 font-semibold flex`}>
                    <div className="label text-[#6b7280] mr-2">Avg. Volume:</div>
                    <div className="value text-[#111827]">
                      {etfDetail?.averageVolume ? `${formatLarge(etfDetail.averageVolume)}` : '-'}
                    </div>
                  </div>

                  <div className={`${styles.fundamentalItem} bg-white p-1 font-semibold flex`}>
                    <div className="label text-[#6b7280] mr-2">Total Expense Ratio:</div>
                    <div className="value text-[#111827]">{etfDetail?.fundInfo?.avg_expense_ratio ?? '-'}</div>
                  </div>

                  <div className={`${styles.fundamentalItem} bg-white p-1 font-semibold flex`}>
                    <div className="label text-[#6b7280] mr-2">Market Cap:</div>
                    <div className="value text-[#111827]">
                      {etfDetail?.marketCap ? `$${formatLarge(etfDetail.marketCap)}` : '-'}
                    </div>
                  </div>

                  <div className={`${styles.fundamentalItem} bg-white p-1 font-semibold flex`}>
                    <div className="label text-[#6b7280] mr-2">52 Wk Range:</div>
                    <div className="value text-[#111827]">
                      {etfDetail?.fiftyTwoWeekLow ? `$${formatPrice(etfDetail.fiftyTwoWeekLow)}` : '-'}
                      {etfDetail?.fiftyTwoWeekHigh ? ` - $${formatPrice(etfDetail.fiftyTwoWeekHigh)}` : '-'}
                    </div>
                  </div>

                  <div className={`${styles.fundamentalItem} bg-white p-1 font-semibold flex`}>
                    <div className="label text-[#6b7280] mr-2">Outstanding Shares:</div>
                    <div className="value text-[#111827]">
                      {etfDetail?.sharesOutstanding ? `${formatLarge(etfDetail.sharesOutstanding)}` : '-'}
                    </div>
                  </div>
                </div>
              </div>

              {!enable_broker_btn && etfDetail?.link && (
                <div className={`block ${styles.aboveLink} ${layout === 'floating' ? 'block' : 'lg:hidden'}`}>
                  <Button as="a" className="w-full" href={etfDetail.link} target="_blank" variant="flat-blue">
                    Learn More
                  </Button>
                </div>
              )}
            </div>
            {enable_nasdaq_stamp && (
              <div className="data-provider flex justify-end gap-2 mt-1 items-center">
                <span className="text-xs text-gray-500">Data provided by </span>
                <img
                  alt="Nasdaq Logo"
                  height="20"
                  src={'https://cdnwp-s3.benzinga.com/wp-content/uploads/2026/03/12153019/NASDAQ_Logo.png'}
                  width="60"
                />
              </div>
            )}
            {etfDetail?.disclosure_link && etfDetail?.disclosure_label && (
              <div className="disclosure-etf">
                <div className="right">
                  <a
                    className={`text-xs italic ${styles.disclosureLink}`}
                    href={etfDetail?.disclosure_link}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {etfDetail?.disclosure_label}
                  </a>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {canExpand && (
        <div className="view-more-wrap text-right cursor-pointer" onClick={handleShowAll} role="button" tabIndex={0}>
          <span className="view-more flex items-center justify-center gap-1 text-sm underline pt-2">
            {showAll ? (
              <>
                View Less
                <Icon className="icon-wrap" icon={faChevronsUp} style={{ fontSize: '12px' }} />
              </>
            ) : (
              <>
                View More
                <Icon className="icon-wrap" icon={faChevronsDown} style={{ fontSize: '12px' }} />
              </>
            )}
          </span>
        </div>
      )}
    </div>
  );
};
